import type { IAdViewChargingPlug, IAdViewThemedImageAsset } from "@finn-no/adview-provider-frontend-types";
import { useLingui } from "@lingui/react";
import { useColorScheme } from "@mobility-item-page/client";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { Tooltip } from "../Tooltip.tsx";
import { evTooltipOpened } from "./evTrackingEvents.ts";

type Props = {
  plugTypes: IAdViewChargingPlug[] | undefined;
};

/**
 * The plugs the car charges with, highlighted above the rows of the charging card (MBJ-459).
 */
export const ChargingPlugs = ({ plugTypes }: Props) => {
  const { i18n } = useLingui();
  const { trackClick } = usePulse();

  if (!plugTypes?.length) {
    return null;
  }

  const title = i18n.t({
    id: "itemPageLayout.ev.chargingCost.plugType.title",
    message: "Plug type",
    comment: "Heading of the plug types on the charging card on electric car ads",
  });

  return (
    <dl className="m-0 pb-16 md:pb-20">
      <dt className="mt-0 mx-0 mb-8 s-text-subtle whitespace-nowrap">
        {title}
        <Tooltip
          tooltip={i18n.t({
            id: "itemPageLayout.ev.chargingCost.plugType.tooltip",
            message:
              "The plug type is the connector the car uses to charge. While most modern electric cars in Europe use a Type 2 CCS connector, older models may use standards like CHAdeMO, Type 1 or Type 2. Knowing your plug type is important for finding compatible public chargers.",
            comment:
              "Tooltip on the plug types on the charging card on electric car ads. The connector names are product names and stay as they are in every language.",
          })}
          label={title}
          // Tracking event: MO_ev105
          onOpen={() => trackClick(evTooltipOpened("plugType"))}
        />
      </dt>
      <dd className="m-0 flex flex-col gap-8">
        {plugTypes.map((plug) => (
          <div key={plug.label} className="flex items-start gap-8">
            {plug.images.map((image) => (
              <PlugImage key={image.light.uri} image={image} />
            ))}
            <span className="font-bold">{plug.label}</span>
          </div>
        ))}
      </dd>
    </dl>
  );
};

const PlugImage = ({ image }: { image: IAdViewThemedImageAsset }) => {
  const colorScheme = useColorScheme();

  return (
    // The artwork is a transparent png, so it sits on a tinted tile: the 8px is padding rather
    // than margin, to keep it inside the tile
    <span className="p-8 rounded-4 s-bg-subtle">
      <img
        src={colorScheme === "dark" ? image.dark.uri : image.light.uri}
        alt={colorScheme === "dark" ? image.dark.altText : image.light.altText}
        width={40}
        height={40}
        className="w-40 h-40"
        loading="lazy"
      />
    </span>
  );
};
